import React, { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const GoogleCallback = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { refresh } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = React.useState("");

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const code = params.get("code");
    const errParam = params.get("error");
    const stateParam = params.get("state");

    if (errParam) {
      setError(errParam === "access_denied" ? "Sign-in cancelled." : `Google error: ${errParam}`);
      return;
    }
    if (!code) {
      setError("Missing authorization code.");
      return;
    }

    (async () => {
      try {
        const redirectUri = window.location.origin + "/auth/google";
        const { data } = await api.post(
          "/auth/google/exchange",
          { code, redirect_uri: redirectUri },
          { withCredentials: true }
        );
        await refresh();
        // Honor returnPath from state if provided, else default
        let target = data.is_admin ? "/admin" : "/account";
        if (stateParam) {
          try {
            const decoded = decodeURIComponent(stateParam);
            // Only allow local paths (must start with /, not //)
            if (decoded.startsWith("/") && !decoded.startsWith("//")) {
              target = decoded;
            }
          } catch {}
        }
        navigate(target, { replace: true, state: { user: data } });
      } catch (e) {
        const msg = e?.response?.data?.detail || "Could not complete sign-in.";
        setError(msg);
      }
    })();
  }, [navigate, params, refresh]);

  if (error) {
    return (
      <div className="min-h-screen bg-spike-elevated flex items-center justify-center px-6" data-testid="google-callback-error">
        <div className="max-w-md w-full bg-spike-elevated border border-red-500/30 p-10 text-center">
          <AlertTriangle className="w-10 h-10 text-red-400 mx-auto mb-4" />
          <h1 className="font-orbitron font-black uppercase text-2xl text-spike-text">
            Sign-in failed
          </h1>
          <p className="mt-3 font-exo text-spike-text-muted text-sm">{error}</p>
          <button
            type="button"
            onClick={() => navigate("/login", { replace: true })}
            className="mt-6 inline-flex items-center gap-2 bg-spike-blue hover:bg-spike-blue-hover text-white font-orbitron font-bold uppercase tracking-[0.22em] text-xs px-6 py-3"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-spike-elevated flex items-center justify-center" data-testid="google-callback">
      <div className="text-center">
        <Loader2 className="w-8 h-8 text-spike-blue-light mx-auto animate-spin" />
        <p className="mt-4 font-orbitron text-xs uppercase tracking-[0.3em] text-spike-text-muted">
          Completing Google sign-in...
        </p>
      </div>
    </div>
  );
};

export default GoogleCallback;
